import type { FirestoreDatabase } from '@firestore-gui/shared';

/** Extract database ID from full resource name (e.g. projects/{project}/databases/{database}). */
export function extractDatabaseId(resourceName: string): string {
  const parts = resourceName.split('/');
  return parts[parts.length - 1];
}

/** Extract database IDs from a list of Firestore databases. */
export function extractDatabaseIds(databases: FirestoreDatabase[]): string[] {
  return databases.map((db) => extractDatabaseId(db.name));
}

/** Default selection: prefer "(default)", otherwise first ID. */
export function getDefaultSelectedId(databaseIds: string[]): string {
  if (databaseIds.includes('(default)')) return '(default)';
  return databaseIds[0] ?? '';
}

/** Whether the error message indicates permission denied. */
export function isPermissionDeniedError(error: string | null): boolean {
  if (!error) return false;
  const lower = error.toLowerCase();
  return (
    lower.includes('api_permission_denied') ||
    lower.includes('permission denied') ||
    lower.includes('firestore admin api')
  );
}

/** Whether the error indicates API not enabled (from message or code). */
export function isApiNotEnabledError(errorMessage: string, code?: string): boolean {
  if (code === 'api_not_enabled') return true;
  const lower = errorMessage.toLowerCase();
  return lower.includes('api_not_enabled') || lower.includes('api is not enabled');
}

/** Whether the error indicates permission denied (from message or code). */
export function isPermissionDeniedFromError(errorMessage: string, code?: string): boolean {
  if (code === 'api_permission_denied') return true;
  const lower = errorMessage.toLowerCase();
  return lower.includes('api_permission_denied') || lower.includes('permission denied');
}

/** Display type for a database by ID (e.g. FIRESTORE_NATIVE). */
export function getDatabaseTypeForId(databases: FirestoreDatabase[], databaseId: string): string {
  const db = databases.find((d) => extractDatabaseId(d.name) === databaseId);
  return db?.type ?? 'FIRESTORE_NATIVE';
}
